import { FileSpreadsheet, FileText, Loader2, Printer } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { usePermissions } from '@/features/auth';
import { getErrorMessage } from '@/shared/api/api-error';
import { recordPdfExport } from '@/shared/api/exports.api';
import { saveFile } from '@/shared/lib/save-file';

type Download = () => Promise<{ blob: Blob; fileName: string }>;

export interface PdfExport {
  /** The list or report name recorded in the audit log, e.g. "report-financial". */
  list: string;
  rows: number;
  countryId?: number;
  filters?: Record<string, string>;
}

/**
 * Export buttons for a list or report (BR §7.8), shown only with
 * reports.export. Excel and CSV come from the Admin API, which applies the
 * same scope and redaction as the screen and records the export; PDF is the
 * browser printing this page (D-21) after the export is recorded. Hidden when
 * printing.
 */
export function ExportActions({
  excel,
  csv,
  pdf,
}: {
  excel?: Download;
  csv?: Download;
  pdf?: PdfExport;
}) {
  const { can } = usePermissions();
  const [busy, setBusy] = useState<'excel' | 'csv' | 'pdf' | null>(null);
  if (!can('reports.export')) return null;

  const download = async (kind: 'excel' | 'csv', fetchFile: Download) => {
    setBusy(kind);
    try {
      const { blob, fileName } = await fetchFile();
      saveFile(blob, fileName);
      toast.success('Export downloaded.');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not export.'));
    } finally {
      setBusy(null);
    }
  };

  const print = async (input: PdfExport) => {
    setBusy('pdf');
    try {
      await recordPdfExport(input);
      window.print();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Could not prepare the PDF.'));
    } finally {
      setBusy(null);
    }
  };

  const spinner = <Loader2 className="size-4 animate-spin" aria-hidden />;

  return (
    <span className="flex flex-wrap gap-2" data-print="hide">
      {excel && (
        <Button
          variant="outline"
          size="sm"
          disabled={busy !== null}
          onClick={() => void download('excel', excel)}
        >
          {busy === 'excel' ? spinner : <FileSpreadsheet className="size-4" aria-hidden />}
          Excel
        </Button>
      )}
      {csv && (
        <Button
          variant="outline"
          size="sm"
          disabled={busy !== null}
          onClick={() => void download('csv', csv)}
        >
          {busy === 'csv' ? spinner : <FileText className="size-4" aria-hidden />}
          CSV
        </Button>
      )}
      {pdf && (
        <Button
          variant="outline"
          size="sm"
          disabled={busy !== null}
          onClick={() => void print(pdf)}
        >
          {busy === 'pdf' ? spinner : <Printer className="size-4" aria-hidden />}
          PDF
        </Button>
      )}
    </span>
  );
}
